import { GraduationCap, Paperclip, Phone, Search, SquarePen, Users, Video } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Avatar } from '@/components/ui/Avatar'
import { IconButton } from '@/components/ui/Button'
import { cn } from '@/utils/cn'
import { shortWhen } from '../format'
import type { ConversationKind, ConversationRow } from '../types'

export function ConversationAvatar({
  kind,
  title,
  src,
  size = 'md',
}: {
  kind: ConversationKind
  title: string
  src?: string | null
  size?: 'sm' | 'md'
}) {
  if (kind === 'direct') return <Avatar name={title} src={src} size={size} />
  const Icon = kind === 'class' ? GraduationCap : Users
  return (
    <span
      className={cn(
        'grid shrink-0 place-items-center rounded-full',
        kind === 'class' ? 'bg-brand-100 text-brand-700' : 'bg-info-100 text-info-700',
        size === 'sm' ? 'size-8 [&_svg]:size-4' : 'size-10 [&_svg]:size-5',
      )}
      aria-hidden="true"
    >
      <Icon />
    </span>
  )
}

interface ConversationListProps {
  rows: ConversationRow[]
  activeId: number | null
  base: string
  onNew: () => void
}

/** The left pane: search, then conversations by latest activity (class rooms marked, unread counts). */
export function ConversationList({ rows, activeId, base, onNew }: ConversationListProps) {
  const { t } = useTranslation('messaging')
  const [q, setQ] = useState('')
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return needle ? rows.filter((r) => r.title.toLowerCase().includes(needle)) : rows
  }, [rows, q])

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-line p-3">
        <label className="relative flex-1">
          <span className="sr-only">{t('Search conversations')}</span>
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-subtle"
            aria-hidden="true"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('Search…')}
            className="h-9 w-full rounded-control border border-line bg-surface-muted pr-3 pl-9 text-sm text-ink placeholder:text-subtle focus:border-brand-400 focus:bg-surface focus:ring-2 focus:ring-brand-100 focus:outline-none"
          />
        </label>
        <IconButton label={t('New conversation')} icon={<SquarePen />} onClick={onNew} variant="primary" />
      </div>
      {shown.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-muted">
          {rows.length === 0 ? t('No conversations yet.') : t('No conversation matches.')}
        </p>
      ) : (
        <ul className="min-h-0 flex-1 overflow-y-auto" aria-label={t('Conversations')}>
          {shown.map((row) => (
            <li key={row.id}>
              <Row row={row} active={row.id === activeId} href={`${base}/${row.id}`} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Row({ row, active, href }: { row: ConversationRow; active: boolean; href: string }) {
  const { t } = useTranslation('messaging')
  const last = row.lastMessage
  let preview: ReactNode = row.kind === 'class' ? t('Class room') : ''
  if (last) {
    const who = last.mine ? t('You') : row.kind !== 'direct' ? last.senderName : null
    const text =
      last.kind === 'call' ? (
        <span className="inline-flex items-center gap-1">
          <Phone className="size-3" aria-hidden="true" />
          {t('Call')}
        </span>
      ) : last.deleted ? (
        <em>{t('Message deleted')}</em>
      ) : last.hasFiles ? (
        <span className="inline-flex items-center gap-1">
          <Paperclip className="size-3" aria-hidden="true" />
          {t('Attachment')}
        </span>
      ) : (
        last.preview
      )
    preview = (
      <>
        {who ? `${t('{{name}}:', { name: who })} ` : null}
        {text}
      </>
    )
  }

  return (
    <Link
      to={href}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center gap-3 border-l-2 px-3 py-3 transition-colors',
        active ? 'border-brand-600 bg-brand-50/70' : 'border-transparent hover:bg-surface-muted',
      )}
    >
      <ConversationAvatar kind={row.kind} title={row.title} src={row.other?.avatarUrl} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <p
            className={cn(
              'min-w-0 flex-1 truncate text-sm',
              row.unread ? 'font-semibold text-ink' : 'font-medium text-ink',
            )}
          >
            {row.title}
          </p>
          {last ? <span className="shrink-0 text-2xs text-subtle">{shortWhen(last.createdAt)}</span> : null}
        </div>
        <div className="mt-0.5 flex items-center gap-2">
          <p className={cn('min-w-0 flex-1 truncate text-xs', row.unread ? 'text-ink-soft' : 'text-muted')}>
            {preview}
          </p>
          {row.ongoingCall ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success-100 px-1.5 py-0.5 text-2xs font-semibold text-success-700">
              {row.ongoingCall.video ? (
                <Video className="size-3" aria-hidden="true" />
              ) : (
                <Phone className="size-3" aria-hidden="true" />
              )}
              {t('Live')}
            </span>
          ) : null}
          {row.unread > 0 ? (
            <span className="grid h-5 min-w-5 shrink-0 place-items-center rounded-full bg-brand-600 px-1.5 text-2xs font-semibold text-white">
              <span className="sr-only">{t('{{count}} unread', { count: row.unread })}</span>
              <span aria-hidden="true">{row.unread > 99 ? '99+' : row.unread}</span>
            </span>
          ) : null}
        </div>
      </div>
    </Link>
  )
}
